import { Component, inject, signal } from '@angular/core';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { Session } from '../../../../../services/session';
import { TeacherService } from '../../../../../services/teacher';
import { ISchools } from '../../../../../interfaces/school';
import { ITeacher, ITeachers } from '../../../../../interfaces/teacher';
import { SchoolService } from '../../../../../services/school';
import { Helpers } from '../../../../../utilities/helpers';
import { FormsModule, NgForm } from '@angular/forms';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import {  NgClass } from '@angular/common';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { ClassService } from '../../../../../services/class';
import { IClasses, ITeacherClassSubjects } from '../../../../../interfaces/class';
import { SubjectService } from '../../../../../services/subject';
import { IQualification } from '../../class';
import { TeacherClassSubjectService } from '../../../../../services/teacher-class-subject';
import { ILevelSubjects } from '../../../../../interfaces/subject';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYears } from '../../../../../interfaces/academic-year';

@Component({
  selector: 'app-teacher-overview',
  imports: [FormsModule, ActionButton, NgClass, LoadingIndicator, NotFoundError, Pagination],
  templateUrl: './teacher-overview.html',
  styleUrl: './teacher-overview.css',
})
export class TeacherOverview {
  constructor(
    private session: Session,
    private teacherService: TeacherService,
    private schoolService: SchoolService,
    private classService: ClassService,
    protected subjectService: SubjectService,
    protected teacherClassSubjectService: TeacherClassSubjectService,
    protected academicYearService: AcademicYearService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }
    this.getTeachers();
    this.getAcademicYears();
  }

  protected searchTeachers: string = '';
  protected teacherStatus: string = '';
  protected currentTeacherFilterType = '';
  protected currentTeacherFilterValue = '';
  protected isFilteringTeachers = signal<boolean>(false);
  protected teacherPagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected schools = signal<ISchools | null>(null);
  protected teachers = signal<ITeachers | null>(null);
  protected filterSchoolId: string = '';
  protected readonly Helpers = Helpers;
  protected isFiltering = signal<boolean>(false);
  protected setTeacherDetail = signal(false);
  protected readonly Number = Number;
  protected classes = signal<IClasses | null>(null);
  protected teacherClassSubjects = signal<ITeacherClassSubjects | null>(null);
  protected changeStatus = signal<boolean>(false);
  protected newStatus: string = '';
  protected validStatuses: string[] = ['ACTIVE', 'INACTIVE', 'DEACTIVATED'];
  protected isEditing = signal<boolean>(false);
  protected editLevel = signal<boolean>(false);
  protected staffNumber: string = '';
  protected hireDate: string = '';
  protected specialization: string = '';
  protected qualification: string = '';
  protected phone: string = '';
  protected employmentType: string = '';
  protected qualifications: IQualification[] | any[] = [];
  protected allowedEmploymentTypes = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY'];
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected assignClass = signal<boolean>(false);
  protected assignSubject = signal<boolean>(false);
  protected isAssigning = signal<boolean>(false);
  protected classList = signal<IClasses | null>(null);
  protected levelSubjects = signal<ILevelSubjects | null>(null);
  protected selectedClassId: string = '';
  protected assignmentRole: string = '';
  protected selectedLevelSubjectId: string = '';
  protected academicYears = signal<IAcademicYears | null>(null);
  protected selectedAcademicYearId: string = '';

  protected selectedTeacher: ITeacher['payload'] = {
    id: '',
    public_id: '',
    user_id: '',
    school_id: '',
    first_name: '',
    last_name: '',
    username: '',
    staff_number: 'N/A',
    hire_date: 'N/A',
    specialization: 'N/A',
    qualification: [],
    phone: 'N/A',
    employment_type: 'N/A',
    status: '',
    created_at: '',
    updated_at: '',
  };

  protected addQualification(form: NgForm): void {
    if (this.qualification.trim() === '') {
      this.flashService.failure('Please add a qualification');
      return;
    }

    const exists = this.qualifications.some(
      (qualification) => qualification.name === this.qualification,
    );

    if (exists) {
      this.flashService.failure('Cannot enter duplicate qualification');
      return;
    }

    const qualificationForm: IQualification = {
      id: crypto.randomUUID(),
      name: this.qualification,
    };

    if (this.qualification != '') {
      this.qualifications.push(qualificationForm);
      form.control.markAsDirty();
      form.control.markAsTouched();
    } else {
      this.flashService.failure('Please enter a qualification');
    }

    this.qualification = '';
  }

  protected deleteQualification(id: string, form: NgForm): void {
    this.qualifications = this.qualifications.filter((i) => i.id !== id);
    form.control.markAsDirty();
    form.control.markAsTouched();
  }

  protected getClasses(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      class_teacher_id: this.selectedTeacher.id,
    };

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classes.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected getTeacherClassSubjects(teacherId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      teacher_id: teacherId,
    };

    this.subjectService
      .getTeacherClassSubjects(`${ApiEndpoints.url}/teacher-class-subject`, params)
      .subscribe({
        next: (res) => {
          this.teacherClassSubjects.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load subjects for teachers. Refresh the page',
          );
        },
      });
  }

  protected openAssignClass(): void {
    this.getClassList(this.selectedAcademicYearId);
    this.assignClass.set(true);
  }

  protected openAssignSubject(): void {
    this.getClassList(this.selectedAcademicYearId);
    this.assignSubject.set(true);
  }

  protected getClassList(academicYearId = this.selectedAcademicYearId): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    if (academicYearId) {
      params['academic_year_id'] = academicYearId;
    }

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classList.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected onAcademicYearChange(): void {
    this.selectedClassId = '';
    this.levelSubjects.set(null);
    this.getClassList();
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected loadLevelSubjects(): void {
    const selectedClass = this.classList()?.payload?.find((c) => c.id === this.selectedClassId);

    if (!selectedClass?.level_id) return;

    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      level_id: selectedClass.level_id,
    };

    this.subjectService.getLevelSubjects(`${ApiEndpoints.url}/level-subject`, params).subscribe({
      next: (res) => {
        this.levelSubjects.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load subjects. Refresh the page',
        );
      },
    });
  }

  protected assignTeacherToClass(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isAssigning.set(true);

    const editForm = {
      class_teacher_id: this.selectedTeacher.id,
    };

    this.classService
      .editClass(`${ApiEndpoints.url}/class/${this.selectedClassId}`, editForm)
      .subscribe({
        next: () => {
          this.isAssigning.set(false);
          this.selectedClassId = '';
          this.assignClass.set(false);
          this.flashService.success('Successfully assigned Class Teacher');
          this.getClasses();
        },
        error: (err) => {
          this.isAssigning.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Error assigning Class Teacher. Try again',
          );
        },
      });
  }

  protected assignTeacherToSubject(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    this.isAssigning.set(true);

    const teacherSubjectForm = {
      school_id: this.schoolId,
      teacher_id: this.selectedTeacher.id,
      level_subject_id: this.selectedLevelSubjectId,
      assignment_role: this.assignmentRole,
      class_id: this.selectedClassId,
    };

    this.teacherClassSubjectService
      .createTeacherClassSubject(`${ApiEndpoints.url}/teacher-class-subject`, teacherSubjectForm)
      .subscribe({
        next: () => {
          this.isAssigning.set(false);
          this.selectedClassId = '';
          this.selectedLevelSubjectId = '';
          this.assignmentRole = '';
          this.assignSubject.set(false);
          this.flashService.success('Teacher assigned successfully.');
          this.getTeacherClassSubjects(this.selectedTeacher.id);
        },
        error: (err) => {
          this.isAssigning.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning Teacher. Try again.');
        },
      });
  }

  protected editTeacherFn(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    let payload: Record<string, any> = {};
    let url: string;

    if (action === 'status') {
      url = `${ApiEndpoints.url}/teacher/${this.selectedTeacher.id}/status`;

      payload = {
        status: this.newStatus,
      };
    } else {
      url = `${ApiEndpoints.url}/teacher/${this.selectedTeacher.id}`;

      const original = this.selectedTeacher;

      if (this.staffNumber.trim() !== original.staff_number) {
        payload['staff_number'] = this.staffNumber.trim();
      }

      const currentHireDate = this.hireDate?.trim() || '';
      const originalHireDate = original.hire_date?.trim() || '';

      if (currentHireDate !== originalHireDate) {
        payload['hire_date'] = currentHireDate;
      }

      if (this.specialization.trim() !== original.specialization) {
        payload['specialization'] = this.specialization.trim();
      }

      const currentPhone = this.phone?.trim() || '';
      const originalPhone = original.phone?.trim() || '';

      if (currentPhone !== originalPhone) {
        payload['phone'] = currentPhone;
      }

      if (this.employmentType !== original.employment_type) {
        payload['employment_type'] = this.employmentType;
      }

      const currentQualifications = this.qualifications.map((q) => q.name);
      const originalQualifications = this.selectedTeacher.qualification;

      if (JSON.stringify(currentQualifications) !== JSON.stringify(originalQualifications)) {
        payload['qualification'] = currentQualifications;
      }
    }

    if (!Object.keys(payload).length) {
      this.flashService.failure('No changes detected');
      return;
    }

    this.isEditing.set(true);

    this.teacherService.editTeacher(url, payload).subscribe({
      next: () => {
        this.isEditing.set(false);
        this.flashService.success('Successfully updated teacher');
        this.getTeachers();
        this.changeStatus.set(false);
        this.editLevel.set(false);
      },
      error: (err) => {
        this.isEditing.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error updating teacher');
      },
    });
  }

  protected getTeachers(
    filterType = this.currentTeacherFilterType,
    value = this.currentTeacherFilterValue,
  ): void {
    this.isFilteringTeachers.set(true);

    this.currentTeacherFilterType = filterType;
    this.currentTeacherFilterValue = value;

    let { page, size } = this.teacherPagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.teacherService.getTeachers(`${ApiEndpoints.url}/teacher`, params).subscribe({
      next: (res) => {
        this.teachers.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load teachers. Refresh the page.',
        );
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected selectTeacher(action: string, payload: ITeacher['payload']): void {
    this.selectedTeacher = payload;
    this.qualifications = [];

    if (action === 'status') {
      this.changeStatus.set(true);
    } else if (action === 'edit') {
      this.staffNumber = payload.staff_number;
      this.hireDate = payload.hire_date.split('T')[0];
      this.specialization = payload.specialization;
      this.employmentType = payload.employment_type;
      this.phone = payload.phone;

      if (payload.qualification.length === 0) {
        this.qualifications = [];
      } else {
        for (let i of payload.qualification) {
          this.qualifications.push({
            id: crypto.randomUUID(),
            name: i,
          });
        }
      }

      this.editLevel.set(true);
    } else {
      this.getClasses();
      this.getTeacherClassSubjects(payload.id);
      this.setTeacherDetail.set(true);
    }
  }

  protected loadTeacherPage(page: number): void {
    this.teacherPagination.update((p) => ({
      ...p,
      page,
    }));

    this.getTeachers();
  }
}
